import { chain, get } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormHelperText } from '@mui/material';

import cx from 'classnames';
import styles from '../Deliveries.module.css';
import Button from '../../ReusableComponents/Button/Button';
import TextInput from '../../ReusableComponents/TextInput/TextInput';

export const DEFAULT_UNITS_PER_BOX = 30;

export const getOrdersQuantity = (orders) =>
  chain(orders).flatMap('purchase_order_lines').sumBy('quantity').value();

const validate = (purchaseOrders, boxes, units) => {
  if (!boxes || !units) return false;

  const ordersQuantity = getOrdersQuantity(purchaseOrders);

  return units * 0.95 <= ordersQuantity && ordersQuantity <= units * 1.05;
};

class BoxesAndUnits extends Component {
  constructor(props) {
    super(props);

    this.defaultUnits = props.superuser
      ? getOrdersQuantity(props.purchaseOrders)
      : 0;
    this.defaultBoxes = props.superuser
      ? Math.ceil(this.defaultUnits / DEFAULT_UNITS_PER_BOX)
      : 0;

    this.originalUnits = get(props, 'delivery.units', this.defaultUnits);
    this.originalBoxes = get(props, 'delivery.boxes', this.defaultBoxes);

    this.usedDefaultUnits = this.originalUnits === 0;
    this.usedDefaultBoxes = this.originalBoxes === 0;

    this.state = {
      boxes: this.originalBoxes,
      units: this.originalUnits,
      confirmPenalty: false,
      warning: null,
      boxesError: null,
      unitsError: null,
    };

    this.onChange = this.onChange.bind(this);
    this.toggleConfirmPenalty = this.toggleConfirmPenalty.bind(this);
    this.next = this.next.bind(this);

    props.onChange(this.state);
  }

  onChange({ target: { name, value } }) {
    const { onChange, superuser } = this.props;
    const state = { [name]: Math.abs(+value) };

    if (
      superuser === false &&
      ((name === 'boxes' && this.validateBoxes(value)) ||
        (name === 'units' && this.validateUnits(value)))
    ) {
      return;
    }

    this.setState(state, () => {
      onChange(this.state);
    });
  }

  validateBoxes(newValue) {
    const { locations, delivery } = this.props;
    const location = locations[delivery.location_id];
    const maxByLocation = location.max_boxes_per_slot;
    let error = null;

    if (newValue > 0 && newValue > maxByLocation) {
      error = `You may only deliver up to ${maxByLocation} boxes.`;
    } else if (
      newValue > this.originalBoxes &&
      this.usedDefaultBoxes === false
    ) {
      error = 'You can only decrease the amount of boxes.';
    }
    this.setState({ boxesError: error });
    return error;
  }

  validateUnits(newValue) {
    const { locations, delivery } = this.props;
    const location = locations[delivery.location_id];
    const maxByLocation = location.max_units_per_vendor_per_day;
    let error = null;

    if (this.usedDefaultUnits && newValue > 0 && newValue > maxByLocation) {
      error = `You may only deliver up to ${maxByLocation} units.`;
    } else if (
      newValue > 0 &&
      newValue > this.originalUnits &&
      this.usedDefaultUnits === false
    ) {
      error = 'You can only decrease the amount of units.';
    }
    this.setState({ unitsError: error });
    return error;
  }

  toggleConfirmPenalty() {
    const { confirmPenalty } = this.state;

    this.setState({ confirmPenalty: !confirmPenalty });
  }

  next() {
    const { next, purchaseOrders, superuser } = this.props;
    const { boxes, units, confirmPenalty } = this.state;
    const isValid = validate(purchaseOrders, boxes, units);

    if (!isValid && !confirmPenalty && !superuser) {
      const warning = `Total number of units entered is outside of the 5% tolerance and is subject to a penalty. Please confirm.`;

      this.setState({ warning });
    } else {
      next();
    }
  }

  render() {
    const { boxes, units, warning, confirmPenalty } = this.state;
    const { back, onClose, purchaseOrders } = this.props;

    return (
      <div className={styles.step}>
        <div className={styles.stepContainer}>
          <div className={cx(styles.stepHeader, styles.stepHeaderRow)}>
            <h3>Enter Boxes and Units For Delivery:</h3>
            <div className={cx(styles.stepDetails, styles.totalUnits)}>
              <div className={cx(styles.stepHeaderDetail)}>
                <span>Total Units on Selected Purchase Orders:</span>
                <span>{getOrdersQuantity(purchaseOrders)}</span>
              </div>
            </div>
          </div>
          <div className={styles.stepBody}>
            <div className={styles.boxesAndUnitsInputs}>
              <TextInput
                error={!!this.state.boxesError}
                fullWidth
                id="delivery-boxes"
                type="number"
                name="boxes"
                onChange={this.onChange}
                value={boxes?.toString()}
                formHelperText={
                  <FormHelperText>{this.state.boxesError}</FormHelperText>
                }
                label="# of Boxes"
              />

              <TextInput
                error={!!this.state.unitsError}
                fullWidth
                id="delivery-units"
                type="number"
                name="units"
                onChange={this.onChange}
                value={units?.toString()}
                label="# of Units"
                formHelperText={
                  <FormHelperText>{this.state.unitsError}</FormHelperText>
                }
              />
            </div>
          </div>

          <div className={styles.warning}>
            {warning}
            {warning && (
              <Checkbox
                color="secondary"
                checked={confirmPenalty}
                onChange={this.toggleConfirmPenalty}
              />
            )}
          </div>

          <div className={styles.stepActions}>
            <Button
              onClick={back}
              color="primary"
              className={styles.backButton}
              variant="outlined"
            >
              Back
            </Button>
            <Button
              onClick={onClose}
              variant="text"
              className={styles.cancelButton}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="white"
              className={styles.nextButton}
              onClick={this.next}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

BoxesAndUnits.propTypes = {
  back: PropTypes.func.isRequired,
  next: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  locations: PropTypes.shape({}).isRequired,
  delivery: PropTypes.shape({
    id: PropTypes.string,
    location_id: PropTypes.string,
  }).isRequired,
  purchaseOrders: PropTypes.shape({}).isRequired,
  superuser: PropTypes.bool,
};

BoxesAndUnits.defaultProps = {
  superuser: false,
};

export default BoxesAndUnits;
